import { useState } from 'react'
import { PencilSquareIcon, TrashIcon, DocumentArrowDownIcon, DevicePhoneMobileIcon, ArrowDownTrayIcon, ListBulletIcon } from '@heroicons/react/24/outline'
import { utils as XLSXUtils, write as XLSXWrite } from 'xlsx'
import JsPDF from 'jspdf'
import 'jspdf-autotable'
import { toast } from 'react-hot-toast'
import { parseDate } from 'utils'
import { showAlert } from 'services/alerts.service'
import { makeRequest } from 'services/request.service'
import TableComponent from 'components/common/TableComponent'
import SelectComponent from 'components/common/SelectComponent'
import ButtonComponent from 'components/common/ButtonComponent'
import ActionCardComponent from 'components/common/ActionCardComponent'
import EditDeviceModal from 'components/modals/EditDeviceModal'
import FarmQRModal from 'components/modals/FarmQRModal'
import DeviceActivitiesModal from 'components/modals/DeviceActivitiesModal'

const DevicesGroupsManagement = ({
  devices,
  devicesGroups,
  activities = [],
  onRefreshDevices,
  onRefreshDevicesGroups,
  user
}) => {
  const [state, setState] = useState({
    selectedDevice: null,
    selectedDeviceGroup: '',
    searchTermDevicesGroups: '',
    showEditModal: false,
    isQrModalOpen: false,
    showActivitiesModal: false,
    selectedDeviceForActivities: null,
    selectedActivityIds: [],
    isSavingActivities: false
  })

  const { selectedDevice, showEditModal, isQrModalOpen, showActivitiesModal } = state

  // Helper for auth headers
  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('secret')}`,
    'Content-Type': 'application/json'
  })

  // Create select options for device groups
  const devicesGroupsSelectOptions = devicesGroups.map(group => ({
    value: group.deviceGroupId.toString(),
    label: group.groupName || 'Unnamed Group'
  }))

  const extractActivityIds = (deviceActivities) => {
    if (!deviceActivities) return []

    let activitiesData = deviceActivities
    if (typeof activitiesData === 'string') {
      try {
        activitiesData = JSON.parse(activitiesData)
      } catch (error) {
        return []
      }
    }

    if (!Array.isArray(activitiesData)) return []

    return activitiesData
      .map(activity => {
        if (typeof activity === 'number') return activity
        if (activity && typeof activity === 'object') return Number(activity.activityId)
        return NaN
      })
      .filter(activityId => Number.isInteger(activityId) && activityId > 0)
  }

  const openActivitiesModal = (device) => {
    setState(s => ({
      ...s,
      selectedDeviceForActivities: device,
      selectedActivityIds: extractActivityIds(device.activities),
      showActivitiesModal: true
    }))
  }

  const closeActivitiesModal = () => {
    setState(s => ({
      ...s,
      showActivitiesModal: false,
      selectedDeviceForActivities: null,
      selectedActivityIds: [],
      isSavingActivities: false
    }))
  }

  const handleToggleActivity = (activityId) => {
    setState(s => {
      const exists = s.selectedActivityIds.includes(activityId)
      return {
        ...s,
        selectedActivityIds: exists
          ? s.selectedActivityIds.filter(id => id !== activityId)
          : [...s.selectedActivityIds, activityId]
      }
    })
  }

  const handleSaveDeviceActivities = async () => {
    if (!state.selectedDeviceForActivities?.deviceId) return

    setState(s => ({ ...s, isSavingActivities: true }))
    try {
      const selectedActivities = state.selectedActivityIds.map(activityId => {
        const activity = activities.find(item => Number(item.activityId) === Number(activityId))
        return {
          activityId: Number(activityId),
          activityName: activity?.activityName
        }
      })

      const response = await makeRequest({
        method: 'PUT',
        url: `/management/devices/activities/${state.selectedDeviceForActivities.deviceId}`,
        data: { activities: selectedActivities },
        headers: getAuthHeaders(),
        alertResult: true
      })

      if (response.status === 200) {
        await onRefreshDevices()
        closeActivitiesModal()
      }
    } catch (error) {
      console.error('Error saving device activities:', error)
      showAlert({ message: 'Failed to save device activities', icon: 'error' })
      setState(s => ({ ...s, isSavingActivities: false }))
    }
  }

  // Map devices with their group names for the top table
  const devicesInGroupsMapping = devices.map(device => {
    const group = devicesGroupsSelectOptions.find(g => String(g.value) === String(device.deviceGroupId))
    return {
      deviceId: device.deviceId,
      type: device.type,
      managerDevice: device.managerDevice,
      identifier: device.identifier,
      deviceGroupId: device.deviceGroupId,
      groupName: group ? group.label : 'Unassigned',
      appVersion: device.appVersion,
      lastSync: device.lastSync,
      activities: device.activities,
      assignedActivities: extractActivityIds(device.activities).length
    }
  })

  // Edit device
  const handleEditDevice = async (device) => {
    try {
      // deviceGroupId only travels when a group is selected — the endpoint takes
      // it as a string and an empty value would fail the integer FK.
      const data = { managerDevice: device.managerDevice }
      if (device.deviceGroupId) data.deviceGroupId = String(device.deviceGroupId)
      const response = await makeRequest({
        method: 'PUT',
        url: `/management/devices/${device.deviceId}`,
        data,
        headers: getAuthHeaders(),
        alertResult: true
      })
      if (response.status === 200) {
        setState(s => ({ ...s, showEditModal: false }))
        await onRefreshDevices()
      }
    } catch (error) {
      console.error('Error editing device:', error)
      setState(s => ({ ...s, showEditModal: false }))
    }
  }

  // Delete device in group (top table)
  const handleDeleteDeviceInGroup = async (device) => {
    try {
      const response = await makeRequest({
        method: 'PATCH',
        url: `/management/devices/${device.deviceId}`,
        headers: getAuthHeaders(),
        data: {},
        alertResult: true
      })

      if (response.status === 200) {
        await onRefreshDevices()
        await onRefreshDevicesGroups()
        showAlert({ message: 'Device deleted successfully', icon: 'success' })
      }
    } catch (err) {
      console.error('Error deleting device:', err)
      showAlert({ message: 'Failed to delete device', icon: 'error' })
    }
  }

  const handleExportExcel = () => {
    try {
      const dataToExport = devices.map(device => ({
        'Device Identifier': device.identifier,
        'Device Type': device.type,
        'Group Name': device.groupName,
        'Manager Device': device.managerDevice,
        'App Version': device.appVersion,
        'Last sync': device.lastSync
      }))

      const worksheet = XLSXUtils.json_to_sheet(dataToExport)
      const workbook = XLSXUtils.book_new()
      XLSXUtils.book_append_sheet(workbook, worksheet, 'Devices')

      const excelBuffer = XLSXWrite(workbook, {
        bookType: 'xlsx',
        type: 'array'
      })

      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `devices_${new Date().toISOString().split('T')[0]}.xlsx`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      window.URL.revokeObjectURL(url)

      toast.success('Excel file downloaded successfully')
    } catch (error) {
      console.error('Error exporting to Excel:', error)
      toast.error('Failed to export Excel file')
    }
  }

  const handleExportPDF = () => {
    try {
      const doc = new JsPDF()

      doc.setFontSize(16)
      doc.text('Devices Report', 14, 15)
      doc.setFontSize(10)
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 25)

      const tableColumn = [
        'Device Identifier',
        'Device Type',
        'Group Name',
        'Manager Device',
        'App Version',
        'Last sync'
      ]

      const tableRows = devices.map(device => [
        device.identifier,
        device.type,
        device.groupName,
        device.managerDevice,
        device.appVersion,
        device.lastSync
      ])

      // @ts-ignore
      doc.autoTable({
        startY: 30,
        head: [tableColumn],
        body: tableRows,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 2
        },
        headStyles: {
          fillColor: [67, 160, 71],
          textColor: 255
        }
      })

      doc.save(`devices_${new Date().toISOString().split('T')[0]}.pdf`)

      toast.success('PDF file downloaded successfully')
    } catch (error) {
      console.error('Error exporting to PDF:', error)
      toast.error('Failed to export PDF file')
    }
  }

  const handleChangeSelect = (e) => {
    setState(s => ({
      ...s,
      selectedDeviceGroup: e.target.value
    }))
  }

  const handleSearchChange = (e) => {
    setState(s => ({ ...s, searchTermDevicesGroups: e.target.value }))
  }

  const devicesGroupsTableData = {
    hasCheckbox: true,
    columns: [
      { key: 'type', label: 'Device Type' },
      { key: 'identifier', label: 'Device Identifier' },
      { key: 'groupName', label: 'Group Name' },
      { key: 'managerDevice', label: 'Manager Device', cell: (value) => value ? 'Yes' : 'No' },
      { key: 'assignedActivities', label: 'Assigned Activities' },
      { key: 'appVersion', label: 'App Version' },
      {
        key: 'lastSync',
        label: 'Last sync',
        cell: (value) => parseDate(value)
      },
      {
        key: 'actions',
        label: 'Manage',
        cell: (value, row) => (
          <div className='flex items-center gap-2'>
            <button
              onClick={() => setState(s => ({ ...s, showEditModal: true, selectedDevice: row }))}
              title='Edit' className='p-1 rounded transition'
            >
              <PencilSquareIcon className='w-5 h-5 text-blue-500' />
            </button>
            <button onClick={() => handleDeleteDeviceInGroup(row)} title='Delete' className='p-1 rounded transition'>
              <TrashIcon className='w-5 h-5 text-red-500' />
            </button>
            <button onClick={() => openActivitiesModal(row)} title='Device Activities' className='p-1 rounded transition'>
              <ListBulletIcon className='w-5 h-5 text-emerald-600' />
            </button>
          </div>
        )
      }
    ],
    rows: [...devicesInGroupsMapping]
  }

  const filteredDevicesGroups = {
    ...devicesGroupsTableData,
    rows: devicesGroupsTableData.rows.filter(row => {
      const searchTerm = state.searchTermDevicesGroups.toLowerCase()

      const isMatchingText =
        row.type.toLowerCase().includes(searchTerm) ||
        row.identifier.toLowerCase().includes(searchTerm) ||
        row.groupName.toLowerCase().includes(searchTerm) ||
        row.appVersion.toLowerCase().includes(searchTerm)

      const isMatchingGroup =
        state.selectedDeviceGroup === '' ||
        row.groupName === devicesGroupsSelectOptions.find(g => g.value === state.selectedDeviceGroup)?.label

      return isMatchingText && isMatchingGroup
    })
  }

  return (
    <>
      {/* Devices in Groups */}
      <ActionCardComponent>
        <input
          type='text'
          placeholder='Search Device Group...'
          value={state.searchTermDevicesGroups}
          onChange={handleSearchChange}
          data-align='left'
          className='py-2 px-3 border rounded-md shadow-sm'
        />
        <SelectComponent
          id='selectedDeviceGroup'
          data-align='left'
          name='selectedDeviceGroup'
          value={state.selectedDeviceGroup || ''}
          onChange={handleChangeSelect}
        >
          <option key='' value=''>All Devices in Groups</option>
          {devicesGroupsSelectOptions.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </SelectComponent>

        <a 
          href="https://green.ittel.com.mx/gf-app.apk"
          download
          className="py-2 rounded-lg shadow-md flex items-center gap-2 px-4 rounded-md min-w-max bg-greenPrimary text-white hover:bg-green-700 transition"
        >
          <ArrowDownTrayIcon className='w-5 h-5' />
          <span className='font-medium'>Download APK</span>
        </a>
        <ButtonComponent onClick={() => setState(s => ({ ...s, isQrModalOpen: true }))}>
          <DevicePhoneMobileIcon className='w-5 h-5' />
          <span className='font-medium'>Register Device</span>
        </ButtonComponent>
        <ButtonComponent onClick={handleExportExcel}>
          <DocumentArrowDownIcon className='w-5 h-5' />
          <span className='font-medium'>Export as Excel</span>
        </ButtonComponent>
        <ButtonComponent onClick={handleExportPDF}>
          <DocumentArrowDownIcon className='w-5 h-5' />
          <span className='font-medium'>Export as PDF</span>
        </ButtonComponent>
      </ActionCardComponent>

      <TableComponent tableData={filteredDevicesGroups} defaultSortColumn={filteredDevicesGroups.columns[6]?.key} defaultSortOrder={filteredDevicesGroups.columns[6]?.orderBy || 'desc'} />

      {/* Modals */}
      <FarmQRModal
        isOpen={isQrModalOpen}
        onClose={() => setState(s => ({ ...s, isQrModalOpen: false }))}
        farmId={user.farmId}
        farmName={user.farmName}
      />

      {showEditModal && (
        <EditDeviceModal
          device={selectedDevice}
          groups={devicesGroupsSelectOptions}
          onClose={() => setState(s => ({ ...s, showEditModal: false, selectedDevice: {} }))}
          onSave={handleEditDevice}
        />
      )}

      <DeviceActivitiesModal
        isOpen={showActivitiesModal}
        device={state.selectedDeviceForActivities}
        activities={activities.filter(activity => activity?.isActive !== false)}
        selectedActivityIds={state.selectedActivityIds}
        onToggleActivity={handleToggleActivity}
        onClose={closeActivitiesModal}
        onSave={handleSaveDeviceActivities}
        isSaving={state.isSavingActivities}
      />
    </>
  )
}

export default DevicesGroupsManagement
